import React, { useEffect, useRef, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { useSelector } from "react-redux";
import { getL1SheetProperty } from "../../../features/reduxworkbook/WorkbookSlice";
import { getColourSchemeByID } from "../../../Colors/ColorPalettes";
import { getSheetControls } from "../../../features/reduxworkbook/WorkbookControllingSlice";
import { formatNumber } from "../Utils/FormatNumbers";
function hasCycle(nodes, links) {
    const visited = new Set();
    const recursionStack = new Set();

    // Create an adjacency list from links
    const adjList = {};
    links.forEach((link) => {
        if (!adjList[link.source]) adjList[link.source] = [];
        adjList[link.source].push(link.target);
    });

    function dfs(node) {
        if (recursionStack.has(node)) return true; // Found a cycle
        if (visited.has(node)) return false;

        visited.add(node);
        recursionStack.add(node);

        // Recurse for each neighbor
        for (const neighbor of adjList[node] || []) {
            if (dfs(neighbor)) return true;
        }

        recursionStack.delete(node);
        return false;
    }

    for (const node of nodes) {
        if (dfs(node.name)) return true; // A cycle was detected
    }

    return false; // No cycles found
}
const SankeyChartComp = ({
    Data,
    Sheetid,
    valueKeys,
    toolTipKeys,
    rowKeys: rawGroupByKeys,
    groupByKeys: rawRowKeys,
}) => {
    const customizationObject = useSelector((state) =>
        getL1SheetProperty(state, Sheetid, "customization")
    );

    const isAnimataionOff = useSelector(
        getSheetControls({ Sheetid: Sheetid, key: "isAnimataionOff" })
    );

    const {
        colorSchemeid,
        labelFontSizeHeatmap,
        labelFontColorHeatmap,
        isLabelVisibleSankey,
        labelFontColorSankey,
        labelFontSizeSankey, isTileVisible, isTitleBold, Title, TitleFontColor, TitleSize, valueAlign
    } = customizationObject;
    // console.log(isLabelVisibleSankey);

    const colorScheme = getColourSchemeByID(colorSchemeid);
    const COLORS = colorScheme?.colors || [];
    // console.log(COLORS);

    const containerRef = useRef(null);

    const rowKeys = rawRowKeys.map((item) => item.responseKey) || [];
    const groupByKeys = rawGroupByKeys.map((item) => item.responseKey) || [];

    const xKey = rowKeys[0];
    const yKey = groupByKeys[0];
    const valueKey = valueKeys?.[0]?.responseKey;
    const valueCaption = valueKeys?.[0]?.caption;

    const sankeyData = useMemo(() => {
        if (!xKey || !yKey || !valueKey || !Data?.length)
            return { nodes: [], links: [] };

        const nodesSet = new Set();
        const linksMap = new Map();

        Data.forEach((item) => {
            const source = item[xKey];
            const target = item[yKey];
            const value = Number(item[valueKey]) || 0;

            if (source && target && value > 0) {
                nodesSet.add(source);
                nodesSet.add(target);

                const key = `${source}__${target}`;
                linksMap.set(key, (linksMap.get(key) || 0) + value);
            }
        });

        const nodes = Array.from(nodesSet).map((name) => ({ name }));
        const links = Array.from(linksMap.entries()).map(([key, value]) => {
            const [source, target] = key.split("__");
            return { source, target, value };
        });

        return { nodes, links };
    }, [Data, xKey, yKey, valueKey]);

    const option = useMemo(() => {
        if (!sankeyData.nodes.length || !sankeyData.links.length) return {};

        // Check for cycle in the data
        const hasCycleInData = hasCycle(sankeyData.nodes, sankeyData.links);
        if (hasCycleInData) {
            console.error(
                "Cycle detected in Sankey data, cannot proceed with rendering!"
            );
            // Handle cycle error by returning an empty configuration or a message
            return {
                tooltip: {
                    backgroundColor: "transparent",
                    borderWidth: 0,
                    padding: 0,
                    extraCssText: "pointer-events: auto;",
                    formatter: () => {
                        return `<div style="font-weight: 600; color: red;">Error: Data contains a cycle and cannot be displayed in a Sankey diagram.</div>`;
                    },
                },
                series: [
                    {

                        type: "sankey",
                        data: [],
                        links: [],
                    },
                ],
            };
        }

        // If no cycle, proceed with the Sankey chart configuration
        // console.log(sankeyData.links, sankeyData);

        return {
            tooltip: {
                backgroundColor: "transparent",
                borderWidth: 0,
                padding: 0,
                extraCssText: "pointer-events: auto;",
                formatter: function (params) {
                    const valueKeyCaption = valueKeys?.[0]?.caption || "Value";
                    const numberFormat = valueKeys?.[0]?.numberFormat;

                    const isEdge = params.dataType === "edge";
                    const formattedValue = formatNumber(params.data?.value, numberFormat);

                    const name = isEdge
                        ? `<div style="font-weight: 600;"> ${params.data.source}</div><div style="font-weight: 600;"> ${params.data.target}</div>`
                        : `<div style="font-weight: 600;">${params.data.name}</div>`;

                    const heading = `<div style="font-weight: 600; font-size: 16px ;">${name}</div>`;
                    const content = `<div style="font-size: 15px; margin-top: 2px;">${valueKeyCaption}: ${formattedValue}</div>`;

                    const additional = toolTipKeys
                        ?.map(({ responseKey, caption, numberFormat }) => {
                            const label = caption || responseKey;
                            const dataItem = Array.isArray(Data) ? Data[0] : Data;
                            const rawValue = dataItem?.[responseKey];
                            const isNumeric =
                                !isNaN(rawValue) && rawValue !== null && rawValue !== "";
                            const value = isNumeric ? Number(rawValue) : rawValue ?? "N/A";
                            const formattedValue = isNumeric
                                ? formatNumber(value, numberFormat)
                                : value;

                            return `<div style="font-size: 15px; color: #4B5563;">${label}: ${formattedValue}</div>`;
                        })
                        .join("");

                    return `
                        <div class="border border-gray-200 rounded bg-white p-[10px]"
                             style="background: rgba(255, 255, 255, 0.9); padding: 10px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); border-radius: 6px;">
                            <div style="color: #374151; display: flex; flex-direction: column; gap: 4px;">
                                ${heading}
                                ${content}
                                ${additional}
                            </div>
                        </div>
                    `;
                },
            },
            series: [
                {
                    top: "0.4%",
                    bottom: "1%",
                    type: "sankey",
                    layout: "none",
                    data: sankeyData.nodes.map((node, index) => ({
                        ...node,
                        itemStyle: {
                            color: COLORS[index % COLORS.length],
                        },
                    })),
                    links: sankeyData.links.map((link) => ({
                        ...link,
                        lineStyle: {
                            color: "gradient",
                            type: "linear",
                            gradient: {
                                type: "linear",
                                x: 0,
                                y: 0,
                                x2: 1,
                                y2: 1,
                                colorStops: [
                                    { offset: 0, color: COLORS[0] },
                                    { offset: 1, color: COLORS[1] },
                                ],
                            },
                        },
                    })),
                    label: {
                        show: true,
                        color: labelFontColorSankey || "#333",
                        fontSize: labelFontSizeSankey || 12,
                    },
                    emphasis: {
                        focus: "adjacency",
                    },
                    itemStyle: {
                        borderWidth: 1,
                        borderColor: "#aaa",
                    },
                },
            ],
        };
    }, [
        sankeyData,
        isLabelVisibleSankey,
        labelFontColorSankey,
        labelFontSizeSankey,
        valueKeys,
        COLORS,
        toolTipKeys,
        Data,
    ]);

    if (!Data?.length || !xKey || !yKey || !valueKey) return null;

    return (
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Title Section */}
            {isTileVisible && (
                <div
                    className={`w-full ${isTitleBold ? "font-bold" : ""} ${valueAlign === "left"
                        ? "text-left"
                        : valueAlign === "right"
                            ? "text-right"
                            : "text-center"
                        }`}
                    style={{
                        fontSize: TitleSize,
                        color: TitleFontColor ?? "#bababa",
                        marginBottom: "0px",     // Ensure no extra space
                        paddingBottom: "0px",    // Remove bottom padding
                    }}
                >
                    {Title || " Title..."}
                </div>
            )}

            {/* Chart Section */}
            <div style={{ flex: 1 }}>
                <ReactECharts
                    option={option}
                    notMerge={true}
                    style={{ height: isTileVisible ? "95%" : "100%", width: "100%" }}
                />
            </div>
        </div>








    );
};

export default SankeyChartComp;
